import React from "react"; 
import { createAppKit } from '@reown/appkit/react'
import { arbitrum, mainnet, polygon} from '@reown/appkit/networks'
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { defineChain } from "@reown/appkit/networks";
import { WagmiProvider  } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

/**START: WEB3 CONFIG */
// 0. Setup queryClient
const queryClient = new QueryClient();
const REOWN_API_HOST = 'api.web3modal.org';

let hasInstalledReownFetchFallback = false;

// 1. Get projectId from https://cloud.reown.com
const projectId = `${import.meta.env.VITE_APP_PROJECT_ID}`;
if (!projectId) {
	throw new Error("Please provide PROJECT ID");
} 

// 2. Create a metadata object - optional
const domain = import.meta.env.VITE_DOMAIN || "react.akedo.gg";
const metadata = {
	name: "Akedo Web Games" ,
	description: "Akedo Web Games",
	url: `https://${domain}`,
	icons: ["https://avatars.githubusercontent.com/u/37784886"],
};

function createFallbackResponse(url) {
	if (url.pathname === '/appkit/v1/config') {
		return new Response(JSON.stringify({ features: [] }), {
			status: 200,
			headers: { 'Content-Type': 'application/json' },
		});
	}

	if (url.pathname === '/appkit/v1/project-limits') {
		return new Response(
			JSON.stringify({
				planLimits: {
					tier: 'none',
					isAboveMauLimit: false,
					isAboveRpcLimit: false,
				},
			}),
			{
				status: 200,
				headers: { 'Content-Type': 'application/json' },
			}
		);
	}

	if (url.pathname === '/projects/v1/origins') {
		return new Response(JSON.stringify({ allowedOrigins: [] }), {
			status: 200,
			headers: { 'Content-Type': 'application/json' },
		});
	}

	if (url.pathname === '/getAnalyticsConfig') {
		return new Response(JSON.stringify({ isAnalyticsEnabled: false }), {
			status: 200,
			headers: { 'Content-Type': 'application/json' },
		});
	}

	return null;
}

function installReownFetchFallback() {
	if (
		hasInstalledReownFetchFallback ||
		typeof window === 'undefined' ||
		typeof window.fetch !== 'function' ||
		typeof Response === 'undefined'
	) {
		return;
	}

	const originalFetch = window.fetch.bind(window);

	window.fetch = async (input, init) => {
		try {
			return await originalFetch(input, init);
		} catch (error) {
			const requestUrl =
				typeof input === 'string' || input instanceof URL
					? new URL(input, window.location.origin)
					: new URL(input.url);

			if (requestUrl.hostname !== REOWN_API_HOST) {
				throw error;
			}

			const fallbackResponse = createFallbackResponse(requestUrl);
			if (!fallbackResponse) {
				throw error;
			}

			return fallbackResponse;
		}
	};

	hasInstalledReownFetchFallback = true;
}

const bnb = defineChain({
	id: 56, // Binance Smart Chain Mainnet Chain ID
	caipNetworkId: "eip155:56", // CAIP-2 compliant network ID
	chainNamespace: "eip155", // EVM-compatible namespace
	name: "Binance Smart Chain",
	nativeCurrency: {
		decimals: 18,
		name: "Binance Coin",
		symbol: "BNB",
	},
	rpcUrls: {
		default: {
			http: ["https://bsc-dataseed.binance.org/"], // Public RPC endpoint
			webSocket: ["wss://bsc-ws-node.nariox.org"], // Public WebSocket endpoint
		},
	},
	blockExplorers: {
		default: {
			name: "BscScan",
			url: "https://bscscan.com/",
		},
	},
	contracts: {
		// Add any relevant contracts here (optional)
	},
});

// 3. Set the networks
const networks = [
	mainnet,
	arbitrum,
	polygon,
	bnb,
];

// 4. Create Wagmi Adapter
const wagmiAdapter = new WagmiAdapter({
	networks,
	projectId,
	ssr: true
});

// 5. Create modal
installReownFetchFallback();

createAppKit({
	adapters: [wagmiAdapter],
	networks,
	projectId,
	metadata,
	features: {
		analytics: true,
		email: false, // default to true
		socials: [],
		emailShowWallets: true, // default to true
	  },		
  })

/**END: WEB3 CONFIG */

export function Web3ModalProvider({ children }) {   
    return (
        <WagmiProvider config={wagmiAdapter.wagmiConfig}>
			<QueryClientProvider client={queryClient}>
          		{children}
		  </QueryClientProvider>
        </WagmiProvider>
    );
}